import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { ViewerItem } from 'presentations/api/schema/user';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import Icon from 'presentations/views/components/atoms/Icon';
import Button from 'presentations/views/components/atoms/Button';
import Select from 'presentations/views/components/atoms/Select';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import TextFieldDateRangePicker from 'presentations/views/components/atoms/TextFieldDateRangePicker';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  gray03,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import styled from '@emotion/styled';
import { useScheduleView } from './hooks';
import { useMemo } from 'react';
import MessageList, { MessageListMain } from '../MessageList';
import SubmitForm from '../SubmitForm';
import { ActiveIndex } from 'presentations/reducers/AssistantWidget';
import ChatFormTemplate from '../ChatFormTemplate';

interface Props {
  viewer: ViewerItem;
  onClickBack: () => void;
}

export default function Component(props: Props) {
  const { state, actions } = useScheduleView({
    viewer: props.viewer,
  });
  const t = translateInComponent([translations]);

  const projectOptions = useMemo(() => {
    return [
      { text: t('All'), value: '' },
      ...(state.projects || []).map((p) => ({
        text: p.name,
        value: p.uid,
      })),
    ];
  }, [state.projects]);

  return (
    <>
      {state.activeIndex === ActiveIndex.ScheduleChat && (
        <>
          <ChatFormTemplate
            header={
              <Header>
                <HeaderLeftItem>
                  <HeaderLeftItemIconButton onClick={props.onClickBack}>
                    <HeaderLeftItemIcon name="chevron-left" />
                  </HeaderLeftItemIconButton>
                  <HeaderTitle>
                    <TitleAvatar src={imagePath('assistant/icon.svg')} />
                    <ChatTitle>{t(`Title`)}</ChatTitle>
                  </HeaderTitle>
                </HeaderLeftItem>
              </Header>
            }
            content={
              <ScheduleSettingContainer>
                <ScheduleContainer>
                  <RangeContainer>
                    <SubTitle>{t('Range')}</SubTitle>
                    <TextFieldDateRangePicker
                      startedAt={state.startedAtFilter}
                      endedAt={state.endedAtFilter}
                      onChange={actions.onChangeDateFilter}
                      locale={state.userProfile?.locale}
                      timeZone={state.userProfile?.timeZone}
                      size="medium"
                      placeholder={t('DateTime')}
                      initializedDateRange={true}
                    />
                  </RangeContainer>

                  <UserContainer>
                    <SubTitle>{t('Member')}</SubTitle>
                    <ScheduleUser>
                      <Group max={6}>
                        {state.selectedUsers.map((user, index) => (
                          <ScheduleUserAvatar
                            key={user.userId}
                            src={user.profileImage}
                          >
                            {user.name.slice(0, 1)}
                          </ScheduleUserAvatar>
                        ))}
                      </Group>
                      <ScheduleUserAddButton onClick={actions.onClickEditUser}>
                        <ScheduleUserAddButtonIcon name="account-plus" />
                      </ScheduleUserAddButton>
                    </ScheduleUser>
                  </UserContainer>
                </ScheduleContainer>

                <ScheduleStartButton
                  onClick={actions.onClickStartScheduling}
                  variant="outlined"
                >
                  {t('Submit')}
                </ScheduleStartButton>
              </ScheduleSettingContainer>
            }
          />
        </>
      )}
      {state.activeIndex === ActiveIndex.ScheduleChatSelectUser && (
        <>
          <ChatFormTemplate
            header={
              <Header>
                <HeaderLeftItem>
                  <HeaderLeftItemIconButton onClick={actions.onClickBack}>
                    <HeaderLeftItemIcon name="chevron-left" />
                  </HeaderLeftItemIconButton>
                  <HeaderTitle>
                    <ChatTitle>{t(`Title Editorial User`)}</ChatTitle>
                  </HeaderTitle>
                </HeaderLeftItem>
              </Header>
            }
            content={
              <UserSelectContainer>
                <ModalContent>
                  <SectionHeader>
                    <SectionTitle>{t('Team Member')}</SectionTitle>
                  </SectionHeader>
                  <ProjectContainer>
                    <StyledSelect
                      name="project"
                      value={state.projectUid}
                      options={projectOptions}
                      onChange={actions.onSelectProject}
                      isDisabled={!projectOptions}
                    />
                    <CheckAll>
                      <NaviCheckbox
                        onChange={actions.onChangeCheckedAllMember}
                        isChecked={state.isCheckedAllMember}
                        isDisabled={
                          state.isLoadingTeamUsers ||
                          state.isProjectLoading ||
                          !projectOptions
                        }
                      />
                      {t('Check All')}
                    </CheckAll>
                  </ProjectContainer>
                  {state.selectableUsers.map((teamUser, index) => {
                    return (
                      <CalendarListItemComponent key={teamUser.id}>
                        <CalendarName>
                          <UserAvatar
                            src={teamUser.profileImage || ''}
                            alt={teamUser.name}
                          />
                          <CalendarNameText>{teamUser.name}</CalendarNameText>
                        </CalendarName>
                        <NaviCheckbox
                          value={teamUser.userId}
                          onChange={() => actions.onSelectUser(teamUser.userId)}
                          isChecked={state.selectedUserUids.includes(
                            teamUser.userId,
                          )}
                          isDisabled={teamUser.userId === props.viewer.uid}
                        />
                      </CalendarListItemComponent>
                    );
                  })}
                  {(state.isLoadingTeamUsers || state.isProjectLoading) && (
                    <>
                      <CalendarListItemComponent>
                        <Skeleton width="100%" height="44px" />
                      </CalendarListItemComponent>
                      <CalendarListItemComponent>
                        <Skeleton width="100%" height="44px" />
                      </CalendarListItemComponent>
                      <CalendarListItemComponent>
                        <Skeleton width="100%" height="44px" />
                      </CalendarListItemComponent>
                    </>
                  )}
                  {!state.isLoadingTeamUsers &&
                    !state.isProjectLoading &&
                    (state.projectMembers || state.teamUsers || []).length ===
                      0 && <NoMember>{t('No team users')}</NoMember>}
                </ModalContent>
              </UserSelectContainer>
            }
          />
        </>
      )}
      {state.activeIndex === ActiveIndex.ScheduleChatMessage && (
        <>
          <ChatFormTemplate
            header={
              <>
                <Header>
                  <HeaderLeftItem>
                    <HeaderLeftItemIconButton onClick={actions.onClickBack}>
                      <HeaderLeftItemIcon name="chevron-left" />
                    </HeaderLeftItemIconButton>
                    <HeaderTitle>
                      <TitleAvatar src={imagePath('assistant/icon.svg')} />
                      <ChatTitle>{t(`Title`)}</ChatTitle>
                    </HeaderTitle>
                  </HeaderLeftItem>
                </Header>
                <ScheduleHeader>
                  <ScheduleUser>
                    <Group max={9}>
                      {state.selectedUsers.map((user, index) => (
                        <ScheduleUserAvatar
                          key={user.id}
                          src={user.profileImage}
                        >
                          {user.name.slice(0, 1)}
                        </ScheduleUserAvatar>
                      ))}
                    </Group>
                  </ScheduleUser>
                </ScheduleHeader>
              </>
            }
            content={
              <MessageListMain>
                <MessageList
                  viewer={props.viewer}
                  isThinking={state.isLoading}
                  messageStream={state.messageStream}
                  messages={state.messages}
                />
              </MessageListMain>
            }
            footer={
              <Footer>
                <SubmitForm
                  isDisabled={state.isNoContent || state.isLoading}
                  isLoading={state.isLoading}
                  onChange={actions.onChangeMessage}
                  onSubmit={actions.onSubmitMessage}
                  content={state.content}
                />
              </Footer>
            }
          />
        </>
      )}
    </>
  );
}
const ScheduleSettingContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${blueGrayPale};
  padding: ${spacing(48)} 0;
`;

const UserSelectContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: ${blueGrayPale};
`;

const ScheduleContainer = styled.div`
  padding: ${spacing(24)} ${spacing(20)};
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  background-color: ${white};
  border-radius: ${borderRadius(12)};
`;

const ScheduleStartButton = styled(Button)`
  margin-top: ${spacing(24)};
  background-color: ${primary};
  border: 1px solid ${white};
  color: ${white};
  :hover {
    background-color: ${primary};
    color: ${whiteFix};
  }
  width: 160px;
  height: 52px;
`;

const Header = styled.div`
  padding: ${spacing(16)} ${spacing(12)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray04};
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;
const HeaderLeftItem = styled.div`
  display: flex;
  justify-content: flex-start;
`;
const HeaderLeftItemIconButton = styled(IconButton)`
  padding: ${spacing(4)};
  margin-right: ${spacing(8)};
`;
const HeaderLeftItemIcon = styled(Icon)`
  color: ${primary};
`;
const ChatTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const TitleAvatar = styled(Avatar)`
  width: 26px;
  height: 26px;
  margin-right: ${spacing(8)};
`;
const Footer = styled.div`
  border-top: 1px solid ${gray04};
`;
const ScheduleHeader = styled.div`
  padding: ${spacing(12)} ${spacing(12)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray04};
`;

const ScheduleUser = styled.div`
  display: flex;
  align-items: center;
`;
const ScheduleUserAvatar = styled(Avatar)`
  width: 36px;
  height: 36px;
`;
const ScheduleUserAddButton = styled.div`
  margin-left: ${spacing(8)};
  width: 36px;
  height: 36px;
  color: ${primary};
  border: 1px dashed ${primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const ScheduleUserAddButtonIcon = styled(Icon)`
  color: ${primary};
  width: 20px !important;
  height: 20px !important;
`;
const RangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${spacing(24)};
  border-bottom: 1px solid ${gray04};
  width: 100%;
  align-items: stretch;
  justify-content: center;
`;

const SubTitle = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;

const UserContainer = styled.div`
  padding-top: ${spacing(24)};
  display: flex;
  flex-direction: column;
`;

const ModalContent = styled.div`
  display: flex;
  width: 100%;
  padding: 0 ${spacing(24)} ${spacing(32)};
  flex-direction: column;
  justify-content: space-between;
  background-color: ${blueGrayPale};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 20px;
  margin: ${spacing(16)} 0;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${gray03};
`;
const SectionTitle = styled.div`
  font-size: ${fontSize(10)};
  flex-grow: 1;
`;
const ProjectContainer = styled.div`
  padding-bottom: ${spacing(16)};
`;
const StyledSelect = styled(Select)`
  width: 100%;
  margin-bottom: ${spacing(12)};

  & .MuiOutlinedInput-input {
    padding: ${spacing(12)} ${spacing(32)} ${spacing(12)} ${spacing(12)};
    font-size: ${fontSize(13)};
  }
`;
const CheckAll = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};

  & > .MuiCheckbox-colorPrimary {
    color: ${black};
  }
`;
const NoMember = styled.div`
  font-size: ${fontSize(13)};
  color: ${gray03};
`;
const NaviCheckbox = styled(Checkbox)`
  padding: 0;
  margin-right: ${spacing(8)};
`;
const CalendarListItemComponent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  :last-of-type {
    margin-bottom: 0;
    padding-bottom: ${spacing(8)};
    border-bottom: 1px solid ${gray04};
  }
  border-top: 1px solid ${gray04};
  padding: ${spacing(8)} 0 0 0;
  margin-bottom: ${spacing(8)};
`;
const CalendarName = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  word-break: break-word;
`;
const CalendarNameText = styled.span`
  /* width: 90px; */
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  ${mediaMax.tablet} {
    width: auto;
  }
`;
const UserAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
  margin-right: ${spacing(8)};
`;
